import { useSyncExternalStore } from 'react';
import { readPreference, writePreference } from './storage.ts';

/**
 * Thème de l'interface. Clair par défaut (docs/ui-redesign.md) ; le sombre est un choix explicite, mémorisé dans le
 * navigateur. Appliqué via `data-theme` sur <html> : les tokens CSS font le reste.
 */
export type Theme = 'light' | 'dark';

const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';
/** Couleur de la barre d'état (PWA, mobile) : celle de l'en-tête. */
const THEME_COLOR: Readonly<Record<Theme, string>> = { light: '#ffffff', dark: '#111c30' };

const listeners = new Set<() => void>();
let current: Theme = 'light';

function apply(theme: Theme): void {
  current = theme;
  document.documentElement.dataset['theme'] = theme;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = THEME_COLOR[theme];
  }
}

/** À appeler une fois avant le premier rendu. */
export function initTheme(): void {
  apply(readPreference('theme', 'light', isTheme));
}

export function setTheme(theme: Theme): void {
  apply(theme);
  writePreference('theme', theme);
  for (const listener of listeners) listener();
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => 'light',
  );
}
